import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { RegionDTO, StateReferenceDTO, ParametersTab, TextureSoil } from '../types/parameters.types';

interface ParametersState {
	regions: RegionDTO[];
	selectedRegionId: string | null;
	stateReference: StateReferenceDTO | null;
	activeTab: ParametersTab;
	selectedTexture: TextureSoil;
	isLoadingRegions: boolean;
	isLoadingReference: boolean;
	error: string | null;

	setRegions: (regions: RegionDTO[]) => void;
	selectRegion: (id: string | null) => void;
	setStateReference: (ref: StateReferenceDTO | null) => void;
	setActiveTab: (tab: ParametersTab) => void;
	setSelectedTexture: (texture: TextureSoil) => void;
	setLoadingRegions: (loading: boolean) => void;
	setLoadingReference: (loading: boolean) => void;
	setError: (error: string | null) => void;
	addRegion: (region: RegionDTO) => void;
	updateRegion: (id: string, name: string, stateCodes: string[]) => void;
	getSelectedRegion: () => RegionDTO | undefined;
	reset: () => void;
}

const initialState = {
	regions: [],
	selectedRegionId: null,
	stateReference: null,
	activeTab: 'macronutrients' as ParametersTab,
	selectedTexture: 'arenosa' as TextureSoil,
	isLoadingRegions: false,
	isLoadingReference: false,
	error: null,
};

export const useParametersStore = create<ParametersState>()(
	devtools(
		(set, get) => ({
			...initialState,

			setRegions: (regions) => set({ regions }),

			selectRegion: (id) =>
				set({
					selectedRegionId: id,
					activeTab: 'macronutrients',
					selectedTexture: 'arenosa',
				}),

			setStateReference: (stateReference) => set({ stateReference }),

			setActiveTab: (activeTab) => set({ activeTab }),

			setSelectedTexture: (selectedTexture) => set({ selectedTexture }),

			setLoadingRegions: (isLoadingRegions) => set({ isLoadingRegions }),

			setLoadingReference: (isLoadingReference) => set({ isLoadingReference }),

			setError: (error) => set({ error }),

			addRegion: (region) =>
				set((state) => ({ regions: [...state.regions, region] })),

			updateRegion: (id, name, stateCodes) =>
				set((state) => ({
					regions: state.regions.map((r) =>
						r._id === id ? { ...r, name, stateCodes, updatedAt: new Date().toISOString() } : r
					),
				})),

			getSelectedRegion: () => {
				const { selectedRegionId, regions } = get();
				if (!selectedRegionId) return undefined;
				return regions.find((r) => r._id === selectedRegionId);
			},

			reset: () => set(initialState),
		}),
		{ name: 'ParametersStore' }
	)
);
